import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Empty, Modal, Uploader, Avatar, Stars } from "@/components/atoms";

export default function Reviews() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.membership_status === "approved";
  const [reviews, setReviews] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ rating: 5, artist_name: "", text: "", photo_url: "" });

  const load = async () => {
    try { const { data } = await api.get("/reviews"); setReviews(data); } catch (e) { setReviews([]); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", form);
      toast.success("Review posted");
      setOpen(false);
      setForm({ rating: 5, artist_name: "", text: "", photo_url: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="reviews-page">
      <div className="px-5 pt-5 flex items-center justify-between">
        <div>
          <span className="eyebrow">Word On The Street</span>
          <h1 className="font-display font-black text-3xl uppercase leading-tight">Reviews</h1>
        </div>
        {canWrite && (
          <button className="atc-btn btn-gold px-3 h-11" onClick={() => setOpen(true)} data-testid="review-add-btn">
            <Plus size={18} /> Write
          </button>
        )}
      </div>

      {reviews === null ? <Loading /> : reviews.length === 0 ? <Empty title="No reviews yet" sub="Be the first to leave one." /> : (
        <div className="px-5 py-4 flex flex-col gap-4">
          {reviews.map((r) => (
            <div key={r.id} className="atc-card p-4 rise-in" data-testid={`review-card-${r.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <Avatar src={r.author_picture} name={r.author_name} size={38} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-bold leading-none">{r.author_name}</p>
                    {r.is_admin ? (
                      <span className="bg-[#8B1E28] text-[#F4F0E6] text-[8px] font-mono2 px-1 rounded uppercase">Staff</span>
                    ) : r.author_badge ? (
                      <span className="bg-[#C7A656] text-[#121212] text-[8px] font-mono2 px-1 rounded uppercase border border-black">{r.author_badge}</span>
                    ) : null}
                  </div>
                  <p className="font-mono2 text-[10px] text-[#554E40] mt-1">for {r.artist_name} · {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <Stars value={r.rating} />
              </div>
              <p className="text-sm">{r.text}</p>
              {r.photo_url && <img src={fileUrl(r.photo_url)} alt="tattoo" className="w-full h-44 object-cover border-2 border-black rounded mt-3" />}
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Write A Review" testid="review-modal">
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <span className="font-mono2 text-xs uppercase">Rating</span>
            <Stars value={form.rating} size={26} onChange={(n) => setForm({ ...form, rating: n })} />
          </div>
          <input className="atc-input" placeholder="Artist name" value={form.artist_name} onChange={(e) => setForm({ ...form, artist_name: e.target.value })} required data-testid="review-artist" />
          <textarea className="atc-input" rows={4} placeholder="How was it?" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required data-testid="review-text" />
          {form.photo_url && <img src={fileUrl(form.photo_url)} alt="preview" className="w-full h-40 object-cover border-2 border-black rounded" />}
          <Uploader onUploaded={(url) => setForm({ ...form, photo_url: url })} label="Add tattoo photo (optional)" testid="review-upload" />
          <button type="submit" className="atc-btn btn-primary" data-testid="review-submit-btn">Post Review</button>
        </form>
      </Modal>
    </div>
  );
}
